/**
 * "Site Pages" — the generic manifest-driven editor for a BESPOKE tenant
 * site's text & images (AGP corporate site, Asta Cafe landing, …). Renders
 * entirely from the tenant's `SiteManifest` in @mlm/shared, so a new bespoke
 * site gets this editor by shipping a manifest — no editor code per tenant.
 *
 * Storage model (`storefront.siteContent`, sparse):
 *   - a field edited back to its default is DELETED from the overlay
 *   - list fields are pristine (defaults, read-only) until "Customize" copies
 *     the defaults in; "Reset to default" deletes the stored list again
 *   - image fields store an uploaded R2 key; empty = the baked default
 * All typed controls — no raw JSON (project convention).
 */
import { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@mlm/ui';
import {
  LIST_TEXT_KEY,
  type SiteContentOverlay,
  type SiteField,
  type SiteManifest,
  type SiteSection,
} from '@mlm/shared';
import { uploadStorefrontAsset, storefrontAssetUrl } from '../../lib/api';
import { RepeatableRows, type RowField } from '../repeatable-rows';

type Props = {
  manifest: SiteManifest;
  overlay: SiteContentOverlay | undefined;
  onChange: (next: SiteContentOverlay | undefined) => void;
  readOnly?: boolean;
};

export function SitePagesEditor({ manifest, overlay, onChange, readOnly }: Props) {
  const [group, setGroup] = useState(manifest.groups[0]?.id ?? '');

  const o: SiteContentOverlay = overlay ?? {};

  /** Emit a cleaned overlay: empty maps dropped; fully empty → undefined. */
  const emit = (next: SiteContentOverlay) => {
    const clean: SiteContentOverlay = {};
    if (next.text && Object.keys(next.text).length) clean.text = next.text;
    if (next.lists && Object.keys(next.lists).length) clean.lists = next.lists;
    if (next.images && Object.keys(next.images).length) clean.images = next.images;
    onChange(Object.keys(clean).length ? clean : undefined);
  };

  const setText = (path: string, value: string, def: string) => {
    const text = { ...o.text };
    if (value === def) delete text[path];
    else text[path] = value;
    emit({ ...o, text });
  };
  const setList = (path: string, rows: Array<Record<string, string>> | undefined) => {
    const lists = { ...o.lists };
    if (rows === undefined) delete lists[path];
    else lists[path] = rows;
    emit({ ...o, lists });
  };
  const setImage = (path: string, key: string | undefined) => {
    const images = { ...o.images };
    if (!key) delete images[path];
    else images[path] = key;
    emit({ ...o, images });
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        The words and pictures of the public {manifest.label}. Every field starts on the site's
        current copy — edit what should change; &ldquo;Reset&rdquo; puts a field back on the
        original. Layout, colors, and navigation stay fixed.
      </p>
      <Tabs value={group} onValueChange={setGroup}>
        <TabsList className="flex-wrap">
          {manifest.groups.map((g) => (
            <TabsTrigger key={g.id} value={g.id}>
              {g.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {manifest.groups.map((g) => (
          <TabsContent key={g.id} value={g.id} className="space-y-4 pt-4">
            {g.sections.map((s) => (
              <SectionCard
                key={s.id}
                groupId={g.id}
                section={s}
                overlay={o}
                readOnly={readOnly}
                setText={setText}
                setList={setList}
                setImage={setImage}
              />
            ))}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

function SectionCard({
  groupId,
  section,
  overlay,
  readOnly,
  setText,
  setList,
  setImage,
}: {
  groupId: string;
  section: SiteSection;
  overlay: SiteContentOverlay;
  readOnly?: boolean;
  setText: (path: string, value: string, def: string) => void;
  setList: (path: string, rows: Array<Record<string, string>> | undefined) => void;
  setImage: (path: string, key: string | undefined) => void;
}) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{section.label}</CardTitle>
        {section.hint && <p className="text-xs text-muted-foreground">{section.hint}</p>}
      </CardHeader>
      <CardContent className="space-y-4">
        {section.fields.map((f) => {
          const path = `${groupId}.${section.id}.${f.id}`;
          return (
            <FieldEditor
              key={f.id}
              path={path}
              field={f}
              overlay={overlay}
              readOnly={readOnly}
              setText={setText}
              setList={setList}
              setImage={setImage}
            />
          );
        })}
      </CardContent>
    </Card>
  );
}

function FieldEditor({
  path,
  field,
  overlay,
  readOnly,
  setText,
  setList,
  setImage,
}: {
  path: string;
  field: SiteField;
  overlay: SiteContentOverlay;
  readOnly?: boolean;
  setText: (path: string, value: string, def: string) => void;
  setList: (path: string, rows: Array<Record<string, string>> | undefined) => void;
  setImage: (path: string, key: string | undefined) => void;
}) {
  if (field.kind === 'text' || field.kind === 'textarea') {
    const stored = overlay.text?.[path];
    const value = stored ?? field.default;
    const dirty = stored !== undefined;
    return (
      <label className="block">
        <FieldHead
          label={field.label}
          dirty={dirty}
          onReset={() => setText(path, field.default, field.default)}
          readOnly={readOnly}
        />
        <div className="mt-1">
          {field.kind === 'text' ? (
            <Input
              className="h-8"
              value={value}
              disabled={readOnly}
              onChange={(e) => setText(path, e.target.value, field.default)}
            />
          ) : (
            <Textarea
              rows={3}
              value={value}
              disabled={readOnly}
              onChange={(e) => setText(path, e.target.value, field.default)}
            />
          )}
        </div>
        {field.hint && <p className="mt-1 text-[11px] text-muted-foreground">{field.hint}</p>}
      </label>
    );
  }

  if (field.kind === 'select') {
    const stored = overlay.text?.[path];
    const value = stored ?? field.default;
    const dirty = stored !== undefined;
    return (
      <div>
        <FieldHead
          label={field.label}
          dirty={dirty}
          onReset={() => setText(path, field.default, field.default)}
          readOnly={readOnly}
        />
        <div className="mt-1 max-w-xs">
          <Select
            value={value}
            onValueChange={(v) => setText(path, v, field.default)}
            disabled={readOnly}
          >
            <SelectTrigger className="h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {field.options.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {field.hint && <p className="mt-1 text-[11px] text-muted-foreground">{field.hint}</p>}
      </div>
    );
  }

  if (field.kind === 'image') {
    const key = overlay.images?.[path];
    const src = key ? storefrontAssetUrl(key) : field.defaultUrl;
    return (
      <div>
        <FieldHead
          label={field.label}
          dirty={!!key}
          onReset={() => setImage(path, undefined)}
          readOnly={readOnly}
          resetLabel="Reset to default image"
        />
        <div className="mt-1">
          <SiteImageControl
            src={src}
            isDefault={!key}
            readOnly={readOnly}
            onUploaded={(k) => setImage(path, k)}
          />
        </div>
        {field.hint && <p className="mt-1 text-[11px] text-muted-foreground">{field.hint}</p>}
      </div>
    );
  }

  // List kinds — pristine shows defaults read-only with one "Customize" button.
  const isParas = field.kind === 'richParas' || field.kind === 'stringList';
  const defaults: Array<Record<string, string>> = isParas
    ? field.default.map((t) => ({ [LIST_TEXT_KEY]: t }))
    : field.default.map((r) => ({ ...r }));
  const stored = overlay.lists?.[path];
  const dirty = stored !== undefined;

  const rowFields: RowField[] = isParas
    ? [
        field.kind === 'richParas'
          ? { kind: 'textarea', key: LIST_TEXT_KEY, label: 'Paragraph', rows: 3 }
          : { kind: 'text', key: LIST_TEXT_KEY, label: 'Text' },
      ]
    : field.itemFields.map((it) =>
        it.kind === 'image'
          ? { kind: 'image', key: it.id, label: it.label }
          : it.kind === 'textarea'
            ? { kind: 'textarea', key: it.id, label: it.label, rows: 3 }
            : { kind: 'text', key: it.id, label: it.label },
      );

  const hint =
    field.kind === 'richParas'
      ? (field.hint ?? '**bold**, *italic*, and "- " at the start of a row for bullets.')
      : undefined;

  return (
    <div>
      <FieldHead
        label={field.label}
        dirty={dirty}
        onReset={() => setList(path, undefined)}
        readOnly={readOnly}
        resetLabel="Reset to default"
      />
      {hint && <p className="mb-1 text-[11px] text-muted-foreground">{hint}</p>}
      {!dirty ? (
        <div className="mt-1 space-y-1 rounded-md border bg-muted/30 p-3">
          {defaults.map((row, i) => (
            <p key={i} className="text-sm text-muted-foreground">
              {rowFields
                .map((rf) => row[rf.key])
                .filter(Boolean)
                .join(' — ')}
            </p>
          ))}
          {!readOnly && (
            <Button
              variant="outline"
              size="sm"
              className="mt-2"
              onClick={() => setList(path, defaults)}
            >
              Customize
            </Button>
          )}
        </div>
      ) : (
        <div className="mt-1">
          <RepeatableRows
            items={stored}
            fields={rowFields}
            fixed={field.kind === 'objectList' && !!field.fixedLength}
            columns={rowFields.length > 2 ? 2 : 1}
            makeBlank={() =>
              Object.fromEntries(rowFields.map((rf) => [rf.key, ''])) as Record<string, string>
            }
            onChange={(rows) => setList(path, rows)}
          />
        </div>
      )}
    </div>
  );
}

function FieldHead({
  label,
  dirty,
  onReset,
  readOnly,
  resetLabel = 'Reset',
}: {
  label: string;
  dirty: boolean;
  onReset: () => void;
  readOnly?: boolean;
  resetLabel?: string;
}) {
  return (
    <span className="flex items-center gap-2 text-xs text-muted-foreground">
      {label}
      {dirty && (
        <>
          <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-medium text-amber-600">
            edited
          </span>
          {!readOnly && (
            <button
              type="button"
              className="text-[11px] underline hover:text-foreground"
              onClick={onReset}
            >
              {resetLabel}
            </button>
          )}
        </>
      )}
    </span>
  );
}

/** Standalone image control: preview (default or uploaded), upload → R2 key. */
function SiteImageControl({
  src,
  isDefault,
  readOnly,
  onUploaded,
}: {
  src: string;
  isDefault: boolean;
  readOnly?: boolean;
  onUploaded: (key: string) => void;
}) {
  const [uploading, setUploading] = useState(false);
  return (
    <div className="flex items-center gap-3">
      <img src={src} alt="" className="h-14 w-24 rounded border bg-white object-cover" />
      {isDefault && (
        <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
          Default
        </span>
      )}
      {!readOnly && (
        <input
          type="file"
          accept="image/*"
          className="text-xs"
          disabled={uploading}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (!f) return;
            setUploading(true);
            uploadStorefrontAsset(f)
              .then(({ key }) => onUploaded(key))
              .finally(() => setUploading(false));
          }}
        />
      )}
      {uploading && <span className="text-xs text-muted-foreground">Uploading…</span>}
    </div>
  );
}
